import { FaEnvelope } from '@react-icons/all-files/fa/FaEnvelope';
import { FaFacebookF } from '@react-icons/all-files/fa/FaFacebookF';
import { FaGlobe } from '@react-icons/all-files/fa/FaGlobe';
import { FaInstagram } from '@react-icons/all-files/fa/FaInstagram';
import { FaLinkedinIn } from '@react-icons/all-files/fa/FaLinkedinIn';
import { FaTwitter } from '@react-icons/all-files/fa/FaTwitter';
import clsx from 'clsx';
import { useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { GatsbyImg, Link } from 'components';
import { Col, Container, Row, Section } from 'components/GridV2';
import { Body, Heading3, Heading4 } from 'components/Typography';
import { LocaleContext } from 'components/rebrand/Layout';

import { IAuthorSanityData } from 'typescriptDefs/pages/learn/author';

import LearnBreadCrumb from './LearnBreadcrumb';

const icons = {
  website: <FaGlobe />,
  email: <FaEnvelope />,
  twitter: <FaTwitter />,
  facebook: <FaFacebookF />,
  instagram: <FaInstagram />,
  linkedin: <FaLinkedinIn />,
};

type Props = {
  author: IAuthorSanityData;
};

const AboutTheAuthor = ({ author }: Props) => {
  const locale = useContext(LocaleContext);
  return (
    <Section className="mt-navMobileHeight bg-blue-light py-32 md:py-64 lg:mt-navHeight">
      <LearnBreadCrumb />
      <Container className="px-30 xs:px-16 md:px-96">
        <Row className="mb-16 flex-wrap">
          <Col>
            <Heading3 className="mb-0">{{ en: `About the author`, fr: `A propos de l'auteur` }[locale]}</Heading3>
          </Col>
        </Row>
        <Row className="flex-col gap-32 md:flex-row">
          <Col className="mb-16 w-full flex-none md:w-1/5">
            <div className="max-w-135 xs:max-w-165 md:max-w-180 lg:max-w-200">
              <GatsbyImg image={author.img?.asset?.gatsbyImageData} className="rounded-[50%]" />
            </div>
          </Col>
          <Col className="flex w-full flex-none flex-col md:w-4/5">
            <div className="mb-16 flex">
              <Heading4
                className={twMerge(
                  clsx(
                    'mb-0 mr-0 self-center font-bold md:self-stretch',
                    author.socialLinks && author.socialLinks.length && 'mr-16',
                  ),
                )}
              >
                {author.name}
              </Heading4>
              <div className="self-end pb-3 [&>:not(:last-child)]:mr-16">
                {author.socialLinks?.map((link, i) => (
                  <Link
                    aria-label={
                      {
                        en: `Link to ${author.name}'s ${link.icon}`,
                        fr: `Lien vers ${author.name}'s ${link.icon}`,
                      }[locale]
                    }
                    key={i}
                    className="inline-block cursor-pointer items-center font-bold text-blue-alt no-underline hover:text-blue-deep"
                    to={link.href}
                  >
                    {icons[link.icon]}
                  </Link>
                ))}
              </div>
            </div>
            <Body className={twMerge(clsx('mb-0', author.expertise && author.expertise.length > 0 && 'mb-16'))}>
              {author.bio}
            </Body>
            {author.expertise && author.expertise.length > 0 && (
              <div className={twMerge(clsx('mb-0', !!author.education && author.education.length > 0 && 'mb-16'))}>
                <Body className="font-bold">{{ en: 'Expertise', fr: 'Expertise' }[locale]}</Body>
                <ul className="m-0">
                  {author.expertise.map((text, i) => (
                    <li key={i}>
                      <Body className="mb-0">{text}</Body>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {author.education && author.education.length > 0 && (
              <div className="mb-0">
                <Body className="font-bold">{{ en: 'Education', fr: 'Éducation' }[locale]}</Body>
                <ul className="m-0">
                  {author.education.map((text, i) => (
                    <li key={i}>
                      <Body className="mb-0">{text}</Body>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </Section>
  );
};

export default AboutTheAuthor;
